import React, { useState } from 'react';
import {
  Button,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import CameraDialog from './components/CameraDialog';
import AdSense from './components/AdSense';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import DatabaseIcon from '@mui/icons-material/Storage';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import InfoIcon from '@mui/icons-material/Info';
import { Facebook as FacebookIcon, Instagram as InstagramIcon } from '@mui/icons-material';
import TikTokIcon from '@mui/icons-material/MusicVideo'; // Using MusicVideo as a substitute for TikTok

const Home = () => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {/* Hero Section */}
      <Paper 
        elevation={0}
        sx={{ 
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 6,
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
          color: 'white',
          padding: { xs: 4, sm: 6 },
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(255, 107, 107, 0.3)',
          mt: 2,
          mb: 3,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '300px',
            height: '300px',
            borderRadius: '50% 40% 60% 30%',
            background: 'rgba(255, 255, 255, 0.1)',
            animation: 'float 6s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-10%',
            width: '200px',
            height: '200px',
            borderRadius: '60% 40% 30% 70%',
            background: 'rgba(255, 255, 255, 0.08)',
            animation: 'float 8s ease-in-out infinite reverse',
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
            '33%': { transform: 'translate(10px, -10px) rotate(120deg)' },
            '66%': { transform: 'translate(-5px, 5px) rotate(240deg)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Skenujte a buďte v klidu! 🎯
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2,
              opacity: 0.95,
              fontWeight: 400,
              lineHeight: 1.4,
              fontSize: { xs: '1rem', sm: '1.2rem' },
            }}
          >
            Jednoduše naskenujte štítek produktu
            <br />
            a okamžitě zjistěte, zda je bezlepkový
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleClickOpen}
            startIcon={<QrCodeScannerIcon sx={{ fontSize: '24px !important' }} />}
            sx={{ 
              py: 2, 
              px: 6,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 50,
              backgroundColor: 'white',
              color: '#ff6b6b',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                transform: 'translateY(-2px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Začít skenování
          </Button>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 2,
              opacity: 0.8,
              fontSize: '0.9rem'
            }}
          >
            Snadné • Rychlé • Užitečné
          </Typography>
        </Box>
      </Paper>

      {/* First ad placement - after substantial content */}
      <Box sx={{ my: 6 }}>
        <AdSense 
          className="main-content-ad"
          adSlot="9006177699"
          style={{ 
            display: 'block',
            width: '100%',
            minHeight: '250px',
            backgroundColor: 'transparent',
          }}
        />
      </Box>

      {/* About Gluten-Free Diet Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Co je bezlepková dieta a proč je důležitá?
        </Typography>
                  <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
            Bezlepková dieta je způsob stravování, při kterém se vylučují všechny potraviny obsahující lepek (gluten v angličtině). 
            Lepek je bílkovina nacházející se v pšenici, žitě, ječmeni a ovsu. Pro osoby s celiaklií je dodržování 
            bezlepkové diety životně důležité, neboť i minimální množství lepku může způsobit vážné zdravotní problémy 
            včetně poškození střevní sliznice, malá absorpce živin a dlouhodobých komplikací. 
            <br />
            Více o zdravotních přínosech 
            se dočtete v sekci <Link href="#health-benefits" sx={{ color: '#ff6b6b', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Zdravotní přínosy bezlepkové diety</Link>.
          </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Kromě lidí s celiaklií může bezlepková dieta pomoci také osobám s neceliaklí glutenovou senzitivitou 
          nebo těm, kteří trpí Crohnovou chorobou či syndromem dráždivého tračníku. Správně vedená bezlepková 
          dieta může významně zlepšit kvalitu života a zmírnit nepříjemné příznaky jako jsou bolesti břicha, 
          nadýmání, únava a kožní problémy.
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Celiaklie postihuje přibližně 1% světové populace a je nejčastějším autoimunitním onemocněním střeva. 
          V České republice žije s celiaklií odhadem 100 000 lidí, přičemž většina z nich o své diagnóze neví. 
          Jedinou účinnou léčbou celiaklie je doživotní dodržování přísné bezlepkové diety.
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
          Naše aplikace Nechci Lepek vám pomůže snadno identifikovat bezlepkové produkty pouhým naskenováním 
          čárového kódu, což vám ušetří čas a zajistí bezpečnost při nákupech. Databáze obsahuje informace 
          o tisících produktů dostupných na českém trhu.
        </Typography>
      </Paper>

      {/* Social Media Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Sledujte nás na sociálních sítích
        </Typography>
        
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4, textAlign: 'center' }}>
          Buďte součástí naší bezlepkové komunity! Sledujte nejnovější tipy, recepty a informace o bezlepkovém životním stylu.
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: { xs: 2, sm: 4 },
          flexWrap: 'wrap',
          mb: 2
        }}>
          <Button
            href="https://www.facebook.com/nechcilepek"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            startIcon={<FacebookIcon />}
            sx={{
              minWidth: '200px',
              color: '#fff',
              backgroundColor: '#1877f2',
              borderRadius: 3,
              py: 2,
              '&:hover': {
                backgroundColor: '#166fe5',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              }
            }}
          >
            Facebook
          </Button>

          <Button
            href="https://www.instagram.com/nechcilepek/"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            startIcon={<InstagramIcon />}
            sx={{
              minWidth: '200px',
              color: '#fff',
              background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
              borderRadius: 3,
              py: 2,
              '&:hover': {
                background: 'linear-gradient(45deg, #e88a2e 0%, #d85f37 25%, #cc233d 50%, #bb205d 75%, #ab167c 100%)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              }
            }}
          >
            Instagram
          </Button>

          <Button
            href="https://www.tiktok.com/@nechcilepek"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            startIcon={<TikTokIcon />}
            sx={{
              minWidth: '200px',
              color: '#fff',
              backgroundColor: '#000',
              borderRadius: 3,
              py: 2,
              '&:hover': {
                backgroundColor: '#333',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              }
            }}
          >
            TikTok
          </Button>
        </Box>

        <Typography sx={{ textAlign: 'center', color: '#666', mt: 3 }}>
          Sdílejte své zkušenosti a zapojte se do diskuze s ostatními členy komunity!
        </Typography>
      </Paper>

      {/* Detailed Gluten-Free Guide Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Komplexní průvodce bezlepkovým životem
        </Typography>
        
        <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#333', fontWeight: 600, mt: 4, mb: 2 }}>
          Potraviny obsahující lepek
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Lepek se nachází v mnoha základních potravinách včetně chleba, těstovin, sušenek, piva a řady 
          zpracovaných potravin. Často se skrývá pod různými názvy jako je pšeničná mouka, bulgur, 
          semolinová mouka, spelda nebo kamut. Pozor si musíte dát také na kontaminaci - produkty vyrobené 
          ve stejných provozech jako lepkové potraviny mohou obsahovat stopy lepku.
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          <strong>Běžné zdroje lepku v potravinách:</strong>
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Pečivo a obiloviny</Typography>
                <List dense>
                  <ListItem>• Pšeničný chléb a rohlíky</ListItem>
                  <ListItem>• Žitné pečivo</ListItem>
                  <ListItem>• Ječné kroupy a slad</ListItem>
                  <ListItem>• Ovesné vločky (pokud nejsou certifikované)</ListItem>
                  <ListItem>• Kuskus a bulgur</ListItem>
                  <ListItem>• Běžné těstoviny</ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Zpracované potraviny</Typography>
                <List dense>
                  <ListItem>• Instantní polévky a omáčky</ListItem>
                  <ListItem>• Hotová jídla</ListItem>
                  <ListItem>• Masové konzervy</ListItem>
                  <ListItem>• Uzeniny a paštiky</ListItem>
                  <ListItem>• Sójové omáčky</ListItem>
                  <ListItem>• Některé druhy koření a směsí</ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Nápoje a sladkosti</Typography>
                <List dense>
                  <ListItem>• Běžné pivo</ListItem>
                  <ListItem>• Sladové nápoje</ListItem>
                  <ListItem>• Sušenky a oplatky</ListItem>
                  <ListItem>• Dorty a zákusky</ListItem>
                  <ListItem>• Čokoládové tyčinky</ListItem>
                  <ListItem>• Zmrzliny s sušenkou</ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Podle studií publikovaných v časopise "Clinical Gastroenterology and Hepatology" může i malé množství 
          lepku (50mg denně) způsobit poškození střevní sliznice u lidí s celiaklií. Pro představu, jeden krajíc 
          běžného chleba obsahuje přibližně 2000mg lepku. Proto je důležité věnovat pozornost i stopovému 
          množství v potravinách.
        </Typography>

        <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#333', fontWeight: 600, mt: 4, mb: 2 }}>
          Bezlepkové alternativy
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Naštěstí existuje široká škála bezlepkových alternativ. Mezi bezpečné obiloviny patří rýže, 
          kukuřice, quinoa, amarant, pohanka, teff a proso. Moderní potravinářský průmysl nabízí bezlepkové 
          verze téměř všech tradičních produktů - od chleba a těstovin až po pizzu a dezerty. Klíčové je 
          vždy ověřit, že produkt je certifikovaně bezlepkový.
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Bezlepkové mouky a směsi</Typography>
                <Typography paragraph>
                  Moderní bezlepkové pečení využívá kombinaci různých mouk pro dosažení optimální textury a chuti:
                </Typography>
                <List dense>
                  <ListItem>• Rýžová mouka - lehká, neutrální chuť</ListItem>
                  <ListItem>• Pohankové mouky - výrazná chuť, bohatá na minerály</ListItem>
                  <ListItem>• Kukuřičná mouka - skvělá pro křupavé pečivo</ListItem>
                  <ListItem>• Mandlová mouka - pro sladké pečivo</ListItem>
                  <ListItem>• Kokosová mouka - nízkokalorická alternativa</ListItem>
                  <ListItem>• Tapioková mouka - výborná pro zahuštění</ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Příklad bezlepkového jídelníčku</Typography>
                <List dense>
                  <ListItem>
                    <strong>Snídaně:</strong>
                  </ListItem>
                  <ListItem>
                    Bezlepková ovesná kaše s ovocem a ořechy
                  </ListItem>
                  <ListItem>
                  <strong>Svačina:</strong>
                  </ListItem>
                  <ListItem>
                    Quinoa salát s grilovanou zeleninou
                  </ListItem>
                  <ListItem>
                  <strong>Oběd:</strong>
                  </ListItem>
                  <ListItem>
                    Rýžové těstoviny s boloňskou omáčkou
                  </ListItem>
                  <ListItem>
                  <strong>Svačina:</strong>
                  </ListItem>
                  <ListItem>
                    Kukuřičné křupky s avokádovým dipem
                  </ListItem>
                  <ListItem>
                  <strong>Večeře:</strong>
                  </ListItem>
                  <ListItem>
                    Pohankové placky s grilovaným masem
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Podle průzkumu Asociace evropských celiaků (AOECS) se kvalita bezlepkových alternativ v posledních 
          letech výrazně zlepšila. Více než 80% respondentů uvedlo, že jsou spokojeni s chutí a texturou 
          moderních bezlepkových výrobků. Navíc, rostoucí trh s bezlepkovými potravinami vede k větší 
          konkurenci a inovacím v oblasti receptur a výrobních postupů.
        </Typography>

        <Typography variant="h5" component="h3" gutterBottom sx={{ color: '#333', fontWeight: 600, mt: 4, mb: 2 }}>
          Nákupní strategie a tipy
        </Typography>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Při nákupech se zaměřte na čtení etiket a hledání symbolů bezlepkovosti. Velké obchodní řetězce 
          dnes nabízejí specializované sekce s bezlepkovými produkty. Online nákup vám umožní pohodlně 
          vybírat z širokého sortimentu s garancí bezlepkovosti. Naše aplikace vám při nákupech výrazně 
          usnadní orientaci v nabídce.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Kde nakupovat</Typography>
                <List dense>
                  <ListItem>
                  <strong>Specializované prodejny:</strong>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      Country Life, Zdravá výživa, DM drogerie
                    </Typography>
                  </ListItem>
                  <ListItem>
                  <strong>Supermarkety:</strong>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      Tesco, Albert, Globus (speciální sekce)
                    </Typography>
                  </ListItem>
                  <ListItem>
                  <strong>Online obchody:</strong>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      Bezlepková.cz, Kleis.cz, Rohlik.cz
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Certifikační značky</Typography>
                <List dense>
                  <ListItem>
                    <strong>Přeškrtnutý klas</strong>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      Mezinárodně uznávaný symbol bezlepkovosti
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <strong>Bez lepku</strong>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      Oficiální označení dle EU norem
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <strong>AOECS</strong>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      Evropská certifikace bezlepkových výrobků
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Praktické tipy</Typography>
                <List dense>
                  <ListItem>
                    <Typography variant="body2">
                      • Vytvořte si seznam ověřených produktů
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      • Nakupujte ve větším množství, když jsou slevy
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      • Kontrolujte data spotřeby a skladujte správně
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body2">
                      • Využívejte věrnostní programy obchodů
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Podle průzkumu Českého statistického úřadu vzrostl v roce 2024 trh s bezlepkovými potravinami o 15%. 
          Tento růst vede k větší dostupnosti produktů a postupnému snižování cen. Například průměrná cena 
          bezlepkového chleba klesla za poslední dva roky o 12%, zatímco kvalita výrobků se dále zlepšuje.
        </Typography>
      </Paper>

      {/* Health Benefits Section */}
      <Paper id="health-benefits" sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Zdravotní přínosy bezlepkové diety
        </Typography>
        
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Pro osoby s celiaklií představuje bezlepková dieta zásadní zlepšení zdravotního stavu. 
          Během několika týdnů až měsíců dochází k regeneraci střevní sliznice, zlepšení vstřebávání 
          živin a postupnému ústupu příznaků. Mnoho pacientů hlásí zvýšenou energii, lepší náladu 
          a zlepšení kožních problémů.
        </Typography>

        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Bezlepková dieta může pomoci také při jiných autoimunitních onemocněních, jako je Hashimotova 
          tyreoiditida nebo revmatoidní artritida. Někteří lidé zaznamenávají zlepšení při migrénách, 
          únavovém syndromu nebo poruchách pozornosti. Je však důležité konzultovat změny stravy 
          s odborníkem.
        </Typography>

        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Přechod na bezlepkovou dietu často vede k větší rozmanitosti stravy, protože lidé objevují 
          nové suroviny a recepty. Zvýšená pozornost věnovaná složení potravin obecně podporuje 
          zdravější stravovací návyky a vědomější vztah k jídlu.
        </Typography>
      </Paper>

      {/* Technology and Innovation Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Technologie ve službách bezlepkového života
        </Typography>
        
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Moderní technologie významně usnadňují život lidem na bezlepkové dietě. Mobilní aplikace 
          jako Nechci Lepek využívají rozsáhlé databáze produktů a umožňují okamžité ověření 
          bezlepkovosti pouhým skenováním čárového kódu. Chceme Vám ušetřit čas při nakupování. 
        </Typography>

        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Naše aplikace je propojena s mezinárodními databázemi potravinových produktů a pravidelně 
          aktualizuje informace o novinkách na trhu. Algoritmy aplikace analyzují složení produktů 
          a identifikují potenciálně problematické ingredience, včetně skrytých zdrojů lepku.
        </Typography>

        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Budoucnost přinese ještě pokročilejší nástroje včetně rozšířené reality pro identifikaci 
          produktů v obchodech, personalizovaných doporučení na základě individuálních potřeb 
          a integrace s chytrými kuchyňskými spotřebiči pro bezpečnou přípravu jídel.
        </Typography>
      </Paper>

      {/* How It Works Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Jak aplikace funguje?
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <QrCodeScannerIcon sx={{ fontSize: 60, color: '#ff6b6b', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  1. Naskenujte kód
                </Typography>
                <Typography>
                  Jednoduše nasměrujte kameru telefonu na čárový kód produktu. 
                  Aplikace automaticky kód rozpozná a načte informace.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <DatabaseIcon sx={{ fontSize: 60, color: '#ff6b6b', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  2. Kontrola databáze
                </Typography>
                <Typography>
                  Aplikace vyhledá produkt v rozsáhlé databázi 
                  a analyzuje složení na přítomnost lepku.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  3. Okamžitý výsledek
                </Typography>
                <Typography>
                  Během vteřin získáte jasnou odpověď - je produkt bezlepkový 
                  nebo obsahuje lepek?
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Features Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Proč zvolit Nechci Lepek?
        </Typography>
        <List sx={{ mt: 3 }}>
          <ListItem>
            <ListItemIcon>
              <SecurityIcon sx={{ color: '#4caf50' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Bezpečnost na prvním místě" 
              secondary="Důkladná kontrola složení produktů s aktualizovanými daty z ověřených zdrojů"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <SpeedIcon sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Rychlé a jednoduché skenování" 
              secondary="Výsledky během několika vteřin bez nutnosti manuálního zadávání informací"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <DatabaseIcon sx={{ color: '#2196f3' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Rozsáhlá databáze produktů" 
              secondary="Přístup k informacím o milionech produktů z databáze."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <HealthAndSafetyIcon sx={{ color: '#e91e63' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Zdravotní bezpečnost" 
              secondary="Speciálně navrženo pro osoby s celiaklií a glutenovou senzitivitou"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Second ad placement - before FAQ */}
      <Box sx={{ my: 6 }}>
        <AdSense 
          className="pre-faq-ad"
          adSlot="1234567890"  // Use a different ad slot
          adFormat="fluid"
          style={{ 
            display: 'block',
            width: '100%',
            minHeight: '280px',
            backgroundColor: 'transparent'
          }}
        />
      </Box>

      {/* FAQ Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3,  }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#ff6b6b', fontWeight: 'bold', textAlign: 'center' }}>
          Často kladené otázky
        </Typography>
        
        <Accordion sx={{ mt: 3, mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Je aplikace zdarma?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Ano, základní funkce aplikace Nechci Lepek jsou zcela zdarma. Můžete skenovat neomezené množství 
              produktů a získávat informace o obsahu lepku bez jakýchkoli poplatků.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Jak přesné jsou informace o produktech?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Informace čerpáme z databáze, která obsahuje ověřené údaje od výrobců. 
              Doporučujeme však vždy zkontrolovat seznam složek na obalu produktu, zejména u produktů 
              označených jako "může obsahovat stopy lepku".
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Co když produkt není v databázi?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Pokud produkt není v naší databázi, aplikace vás na to upozorní. V takovém případě 
              doporučujeme pečlivě si přečíst seznam složek na obalu nebo kontaktovat výrobce pro 
              ověření bezlepkovosti produktu.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Mohu aplikaci používat offline?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Pro vyhledání informací o produktech je potřeba internetové připojení, protože data 
              se načítají z online databáze. Bez připojení k internetu aplikace nebude schopna 
              poskytovat aktuální informace o produktech.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Je aplikace vhodná pro osoby s celiaklií?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Aplikace je navržena s ohledem na potřeby osob s celiaklií a poskytuje důležité informace 
              o obsahu lepku v produktech. Přesto doporučujeme vždy zkontrolovat informace s lékařem 
              nebo nutričním specialistou a pečlivě číst etikety produktů.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Important Notice */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, bgcolor: '#fff3e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoIcon sx={{ color: '#ff9800', mt: 0.5 }} />
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#e65100', fontWeight: 'bold' }}>
              Důležité upozornění
            </Typography>
            <Typography paragraph>
              Aplikace Nechci Lepek slouží pouze pro informační účely a nemůže nahradit odbornou lékařskou 
              konzultaci. Vždy si pečlivě přečtěte seznam složek na obalu produktu a v případě pochybností 
              kontaktujte výrobce nebo svého lékaře.
            </Typography>
            <Typography>
              Pro osoby s vážnými alergiemi nebo celiaklií doporučujeme dvojitou kontrolu všech informací 
              a konzultaci s odborníkem na výživu.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <CameraDialog open={open} onClose={handleClose} />
    </>
  );
};

export default Home;
